"use client";

import { useBooking } from "@/contexts/BookingContext";
import { Button } from "@/components/ui/button";
import {
  Clock,
  Calendar,
  Crown,
  Wallet,
  Tag,
  Trash2,
  Gift,
} from "lucide-react";
import { formatPrice, formatDuration } from "@/lib/utils";
import { useState, useEffect, useRef } from "react";
import { useAppointmentPreview } from "@/lib/apollo/hooks/booking-edge";
import { BookingStep } from "@/types/booking";

interface CartSummaryProps {
  customButton?: {
    text: string;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    className?: string;
    disabled?: boolean;
  };

  showDateTime?: boolean;
  showButton?: boolean;
  showLocation?: boolean;
  showMembershipBanner?: boolean;
  showPromoCode?: boolean;
  showFullBreakdown?: boolean;
  title?: string;
  cartType?: "services" | "vouchers";
}

export default function CartSummary({
  customButton,
  title = "Booking summary",
  showDateTime = false,
  showButton = false,
  showLocation = false,
  showMembershipBanner = false,
  showPromoCode = false,
  cartType,
}: CartSummaryProps) {
  const {
    businessInfo,
    selectedServices,
    selectedPackages,
    giftCardCart,
    appointmentDateTime,
    selectedSalon,
    selectedStaff,
    serviceClient,
    addToast,
    priceSummary,
    setPriceSummary,
    promoCode,
    setPromoCode,
    setVoucherToBeRedeemed,
    setSelectedServices,
    setAvailableServiceStaffs,
    voucherToBeRedeemed,
    deleteService,
    handleNextStep,
    step,
    updateGiftCardCartQuantity,
    clearGiftCardCart,
  } = useBooking();

  const [subtotal, setSubtotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [promoCodeInput, setPromoCodeInput] = useState("");
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const lastRequestRef = useRef<string>("");

  // GraphQL mutation for appointment preview
  const [previewAppointment] = useAppointmentPreview();

  // Service IDs for tracking changes (staff data comes from selectedSalon context)
  const serviceIds = selectedServices.map((service) => service.id);

  const cartCount =
    selectedServices.length + selectedPackages.length + (giftCardCart ? 1 : 0);
  const currency = businessInfo?.country?.currency[0]?.symbol || "₦";

  // Membership discount logic
  const membershipServices =
    (serviceClient as any)?.clientMembership?.membership?.services?.map(
      (s: any) => s.id
    ) || [];
  const membershipRewardPercentage =
    (serviceClient as any)?.clientMembership?.membership?.rewardPercentage || 0;
  const membershipStatus = (serviceClient as any)?.clientMembership
    ?.membershipStatus;

  const getServicePrice = (service: any) => {
    const basePrice = service.staffPrice || service.price || 0;
    const isCoveredByMembership =
      membershipStatus === "live" &&
      membershipServices.includes(service.id) &&
      membershipRewardPercentage > 0;

    if (isCoveredByMembership) {
      const discountAmount = basePrice * (membershipRewardPercentage / 100);
      return basePrice - discountAmount;
    }

    return basePrice;
  };

  const getServiceOriginalPrice = (service: any) => {
    return service.staffPrice || service.price || 0;
  };

  const isServiceCoveredByMembership = (serviceId: string) => {
    return (
      membershipStatus === "live" &&
      membershipServices.includes(serviceId) &&
      membershipRewardPercentage > 0
    );
  };

  // Calculate base prices
  useEffect(() => {
    // Check membership details for discount calculation
    const membershipServices =
      (serviceClient as any)?.clientMembership?.membership?.services?.map(
        (s: any) => s.id
      ) || [];
    const membershipRewardPercentage =
      (serviceClient as any)?.clientMembership?.membership?.rewardPercentage ||
      0;
    const membershipStatus = (serviceClient as any)?.clientMembership
      ?.membershipStatus;

    const servicesTotal = selectedServices.reduce((sum, service) => {
      const quantity = service.quantity || 1;
      const originalPrice = service.staffPrice || service.price || 0;

      // Check if this service is covered by membership
      const isCoveredByMembership =
        membershipStatus === "live" &&
        membershipServices.includes(service.id) &&
        membershipRewardPercentage > 0;

      // Apply membership discount if applicable
      const price = isCoveredByMembership
        ? originalPrice - originalPrice * (membershipRewardPercentage / 100)
        : originalPrice;

      return sum + price * quantity;
    }, 0);

    const packagesTotal = selectedPackages.reduce((sum, pkg) => {
      const quantity = pkg.quantity || 1;
      const price =
        (pkg.usesCustomPrice ? pkg.customPrice : pkg.totalPrice) || 0;
      return sum + price * quantity;
    }, 0);

    const giftCardTotal = giftCardCart
      ? (giftCardCart.amount || 0) * (giftCardCart.quantity || 1)
      : 0;

    const baseTotal = servicesTotal + packagesTotal + giftCardTotal;
    setSubtotal(baseTotal);

    // Calculate total with all discounts (excluding processing fee which depends on payment provider)
    if (priceSummary) {
      // Start with subtotal and add/subtract all the adjustments
      let calculatedTotal = baseTotal;

      // Add tax if applicable, tax is visible, and pricing is NOT tax-inclusive
      // When taxInclusivePricing is true, the displayed prices already include tax,
      // so we don't add tax on top of the subtotal
      if (
        priceSummary.taxAmount &&
        priceSummary.taxAmount > 0 &&
        selectedSalon?.isTaxVisible &&
        !selectedSalon?.taxInclusivePricing
      ) {
        calculatedTotal += priceSummary.taxAmount;
      }

      // Note: Processing fee is NOT included here as it depends on payment provider selection

      // Subtract promo discount if applicable
      if (
        priceSummary.appointmentPromo?.amount &&
        priceSummary.appointmentPromo.amount > 0
      ) {
        calculatedTotal -= priceSummary.appointmentPromo.amount;
      }

      // Note: Membership discount is already applied in the baseTotal calculation above

      setTotal(calculatedTotal);
    } else {
      setTotal(baseTotal);
    }
  }, [
    selectedServices,
    selectedPackages,
    giftCardCart,
    priceSummary,
    serviceClient,
    selectedSalon?.isTaxVisible,
    selectedSalon?.taxInclusivePricing,
  ]);

  // Preview appointment price when client details are available
  const previewPrice = async () => {
    if (!serviceClient || !appointmentDateTime || selectedServices.length === 0)
      return;
    if (!selectedSalon?.id) return;

    // Ensure we have valid client data
    if (!serviceClient.phone || !serviceClient.countryCode) {
      return;
    }

    // Create appointment services data with startAt for each service
    let currentStartTime = appointmentDateTime;
    const services = selectedServices.map((service, index) => {
      if (index > 0) {
        // Calculate start time for subsequent services based on previous service duration
        const prevService = selectedServices[index - 1];
        const prevDuration = prevService.duration || 30;
        const prevStartDate = new Date(currentStartTime);
        prevStartDate.setMinutes(prevStartDate.getMinutes() + prevDuration);
        currentStartTime = prevStartDate.toISOString();
      }

      // Extract original service ID and variant ID for appointment creation
      let originalServiceId = service.id;
      let serviceVariantId = null;

      if (service.variantId && service.id.includes("-")) {
        // This is a variant, extract the original service ID
        originalServiceId = service.id.slice(
          0,
          -(service.variantId.length + 1)
        ); // +1 for the dash
        serviceVariantId = service.variantId;
      }

      return {
        serviceId: originalServiceId,
        ...(serviceVariantId && { serviceVariantId }),
        price: service.price,
        staffIds: service.staff
          ? [service.staff.id]
          : selectedStaff
          ? [selectedStaff.id]
          : [],
        quantity: service.quantity || 1,
        startAt: currentStartTime,
      };
    });

    const requestData = {
      salonId: selectedSalon?.id,
      client: {
        phone: serviceClient.phone,
        countryCode: serviceClient.countryCode,
      },
      services,
      paymentMethod: "card",
      ...(promoCode && { promoCode }),
    };

    // Create request hash to avoid duplicate calls
    const requestHash = JSON.stringify({
      services: services.map((s) => ({
        id: s.serviceId,
        staffIds: s.staffIds,
        quantity: s.quantity,
      })),
      promoCode,
      clientPhone: serviceClient.phone,
    });

    if (requestHash === lastRequestRef.current) return;
    lastRequestRef.current = requestHash;

    try {
      const { data } = await previewAppointment({
        input: requestData,
      });

      if (data.appointmentPreview.status === 400) {
        addToast({
          message: data.appointmentPreview.errors[0].message,
          variant: "error",
        });
        setPromoCode(null);
        setIsApplyingPromo(false);
        setPromoCodeInput("");
        return;
      }

      if (data?.appointmentPreview?.errors?.length > 0) {
        // Handle GraphQL errors
        const errorMessage =
          data.appointmentPreview.errors[0]?.message ||
          "Failed to calculate pricing";
        addToast({
          message: errorMessage,
          variant: "error",
        });
        return;
      }

      if (data?.appointmentPreview?.data) {
        const preview = data.appointmentPreview.data;
        setPriceSummary(preview);
        setSubtotal(
          preview.subTotal || preview.totalServicesAmount || subtotal
        );
        setTotal(preview.totalPaid || preview.totalPrice || total);

        // Handle promo code validation
        if (promoCode && preview.appointmentPromo?.amount > 0) {
          addToast({
            message: "Promo code applied successfully",
            variant: "success",
          });
        } else if (
          promoCode &&
          (!preview.appointmentPromo || preview.appointmentPromo.amount === 0)
        ) {
          setPromoCode(null);
          addToast({
            message: "Invalid promo code",
            variant: "error",
          });
        }
      }
    } catch (error) {
      console.error("Error previewing appointment:", error);
      addToast({
        message: "Unable to calculate pricing. Please refresh the page.",
        variant: "error",
      });
    }
  };

  useEffect(() => {
    if (promoCode) {
      setPromoCodeInput(promoCode);
      // Only try to apply if we have the required context for validation
      if (serviceClient && appointmentDateTime && selectedServices.length > 0) {
        handleApplyPromo();
      }
      // If we don't have the context yet, the promo code will be shown as "pending validation"
      // and will be validated when the user proceeds to the next step
    }
  }, [promoCode]);

  // Update available service staffs from selectedSalon
  useEffect(() => {
    if (selectedSalon?.salonStaffs) {
      const activeStaffs = selectedSalon.salonStaffs.filter(
        (staff) => staff.active && staff.canServeCustomers
      );
      setAvailableServiceStaffs(activeStaffs);
    }
  }, [selectedSalon, setAvailableServiceStaffs]);

  // Preview price when relevant data changes
  useEffect(() => {
    if (serviceClient && appointmentDateTime && selectedServices.length > 0) {
      previewPrice();
    }
  }, [serviceClient, appointmentDateTime, selectedServices, promoCode]);

  const handleApplyPromo = async () => {
    if (!promoCodeInput.trim()) return;

    setIsApplyingPromo(true);

    // First, set the promo code in the context
    setPromoCode(promoCodeInput.trim());

    // Then try to validate it if we have the required context
    if (serviceClient && appointmentDateTime && selectedServices.length > 0) {
      try {
        await previewPrice();
      } catch (error) {
        console.error("Error applying promo code:", error);
        addToast({
          message: "Error validating promo code. Please try again.",
          variant: "error",
        });
      }
    } else {
      // If we don't have the context yet, show success message
      addToast({
        message: `Promo code "${promoCodeInput.trim()}" applied! Add services to see the discount.`,
        variant: "success",
      });
    }

    setTimeout(() => {
      setIsApplyingPromo(false);
    }, 1000);
  };

  const handleRemovePromo = () => {
    setPromoCode(null);
    setPromoCodeInput("");
    previewPrice(); // Re-preview without promo
  };

  const handleRemoveVoucher = () => {
    setVoucherToBeRedeemed(null);
    setSelectedServices([]);
    previewPrice(); // Re-preview without promo
  };

  // Calculate totals (fallback when no priceSummary)
  const totalDuration = selectedServices.reduce(
    (sum, service) => sum + (service.duration || 0),
    0
  );
  const totalOriginalPrice =
    selectedServices.reduce(
      (sum, service) =>
        sum + getServiceOriginalPrice(service) * (service.quantity || 1),
      0
    ) +
    selectedPackages.reduce(
      (sum, p) => sum + p.totalPrice * (p.quantity || 1),
      0
    );
  const totalPrice =
    selectedServices.reduce(
      (sum, service) =>
        sum + getServicePrice(service) * (service.quantity || 1),
      0
    ) +
    selectedPackages.reduce(
      (sum, p) => sum + p.totalPrice * (p.quantity || 1),
      0
    );
  const totalDiscount = totalOriginalPrice - totalPrice;

  // Format appointment date and time
  const formatAppointmentDate = () => {
    if (!appointmentDateTime) return "";
    const date = new Date(appointmentDateTime);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatAppointmentTime = () => {
    if (!appointmentDateTime) return "";
    const date = new Date(appointmentDateTime);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const hasDepositAmount =
    priceSummary?.depositAmount && priceSummary.depositAmount > 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-grey-100 max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col">
      <div className="p-4 border-b border-grey-100 flex-shrink-0">
        <h3 className="text-lg font-semibold text-grey-900 font-heading">
          {title}
        </h3>
      </div>

      {/* Membership Banner */}
      {showMembershipBanner &&
        serviceClient?.isExistingClient &&
        (serviceClient as any)?.clientMembership && (
          <div className="p-4 border-b border-grey-100 flex-shrink-0">
            <div className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-300 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Crown className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-amber-900 font-medium">
                    {(serviceClient as any).clientMembership.membership.name}{" "}
                    Member
                    {(serviceClient as any).clientMembership.membership
                      .rewardPercentage > 0 && (
                      <span className="ml-2 px-2 py-1 bg-amber-200 text-amber-900 text-xs font-bold rounded">
                        {
                          (serviceClient as any).clientMembership.membership
                            .rewardPercentage
                        }
                        % OFF
                      </span>
                    )}
                  </p>
                  {(serviceClient as any)?.membershipWalletBalance > 0 && (
                    <div className="flex items-center gap-2 mt-2">
                      <Wallet className="h-4 w-4 text-amber-600" />
                      <span className="text-sm text-amber-800">
                        Membership Wallet:{" "}
                        {formatPrice(
                          (serviceClient as any).membershipWalletBalance,
                          currency
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Date & Time Section */}
      {appointmentDateTime && showDateTime && (
        <div className="p-4 border-b border-grey-100 flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-grey-400" />
              <span className="text-sm text-grey-900">
                {formatAppointmentDate()}
              </span>
            </div>
            {step > BookingStep.TIME_AND_DATE && (
              <button
                onClick={() => handleNextStep(BookingStep.TIME_AND_DATE)}
                className="text-xs font-medium text-green-600 hover:text-green-700 transition-colors"
              >
                Change
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Clock className="h-4 w-4 text-grey-400" />
            <span className="text-sm text-grey-900">
              {formatAppointmentTime()}
            </span>
          </div>
        </div>
      )}

      {/* Location Section */}
      {selectedSalon && showLocation && (
        <div className="p-4 border-b border-grey-100 flex-shrink-0">
          <h4 className="text-sm font-medium text-grey-700 mb-2">Location</h4>
          <p className="text-sm text-grey-600">{selectedSalon?.address}</p>
        </div>
      )}

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto">
        {cartCount === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-grey-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <Clock className="h-8 w-8 text-green-300" />
            </div>
            <p className="text-grey-600">No services selected</p>
            <p className="text-sm text-grey-500">
              Choose services to begin booking
            </p>
          </div>
        ) : (
          <div className="p-4">
            {voucherToBeRedeemed && (
              <div className="mt-4 mb-4 flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Gift className="h-4 w-4 text-green-600" />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-green-800">
                      {voucherToBeRedeemed.code}
                    </span>
                    {!priceSummary?.appointmentPromo && (
                      <span className="text-xs text-green-600">
                        Redeeming voucher
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {priceSummary?.appointmentPromo?.amount && (
                    <span className="text-sm font-medium text-green-800">
                      -
                      {formatPrice(
                        priceSummary.appointmentPromo.amount,
                        currency
                      )}
                    </span>
                  )}
                  <button
                    onClick={handleRemoveVoucher}
                    className="p-1 hover:bg-green-100 rounded"
                  >
                    <Trash2 className="h-4 w-4 text-green-600" />
                  </button>
                </div>
              </div>
            )}
            <h4 className="text-sm font-medium text-grey-700 mb-3">
              {giftCardCart
                ? "Gift card"
                : selectedServices && selectedServices?.length
                ? "Services"
                : selectedPackages && selectedPackages?.length
                ? "Vouchers"
                : ""}{" "}
            </h4>
            <div className="space-y-3">
              {giftCardCart && (
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-grey-900">Gift card</p>
                    {giftCardCart.message?.trim() && (
                      <p className="text-xs text-grey-500 line-clamp-2">
                        {giftCardCart.message}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex items-start gap-2">
                    <p className="text-sm font-medium text-grey-900">
                      {formatPrice(
                        (giftCardCart.amount || 0) * (giftCardCart.quantity || 1),
                        currency
                      )}
                    </p>
                    <button
                      type="button"
                      onClick={clearGiftCardCart}
                      className="p-1 hover:bg-red-50 rounded transition-colors"
                      title="Remove gift card"
                    >
                      <Trash2 className="h-4 w-4 text-red-500 hover:text-red-600" />
                    </button>
                  </div>
                </div>
              )}
              {selectedServices.map((service) => {
                const isCovered = isServiceCoveredByMembership(service.id);
                const originalPrice =
                  getServiceOriginalPrice(service) * (service.quantity || 1);
                const discountedPrice =
                  getServicePrice(service) * (service.quantity || 1);

                return (
                  <div
                    key={service.id}
                    className="flex justify-between items-start"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-grey-900">
                          {service.name}
                        </p>
                        {isCovered && (
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-xs font-semibold rounded">
                            {membershipRewardPercentage}% OFF
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-grey-500">
                        {formatDuration(service.duration)}
                        {` with `}
                        <span className="text-green-300 font-medium">
                          {service.staff?.user?.firstName ||
                            selectedStaff?.user?.firstName ||
                            "Any Professional"}
                        </span>
                      </p>
                    </div>
                    <div className="text-right">
                      {service.pricingType === "starting_at" && (
                        <p className="text-xs font-medium text-grey-600 uppercase tracking-wide">
                          From
                        </p>
                      )}
                      {isCovered ? (
                        <>
                          <p className="text-sm font-medium text-grey-900">
                            {formatPrice(discountedPrice, currency)}
                          </p>
                          <p className="text-xs text-grey-400 line-through">
                            {formatPrice(originalPrice, currency)}
                          </p>
                        </>
                      ) : (
                        <p className="text-sm font-medium text-grey-900">
                          {formatPrice(originalPrice, currency)}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => deleteService(service.id)}
                      className="p-1 hover:bg-red-50 rounded transition-colors"
                      title="Remove service"
                    >
                      <Trash2 className="h-4 w-4 text-red-500 hover:text-red-600" />
                    </button>
                  </div>
                );
              })}
              {selectedPackages.map((pkg) => (
                <div key={pkg.id} className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-grey-900">
                      {pkg.name}
                    </p>
                    <p className="text-xs text-grey-500">Package</p>
                  </div>
                  {/* <p className="text-sm font-medium text-grey-900">
                    {formatPrice(
                      pkg.totalPrice * (pkg.quantity || 1),
                      currency
                    )}
                  </p> */}
                </div>
              ))}
            </div>

            {/* Promo Code Section */}
            {showPromoCode && !promoCode && (
              <div className="mt-4 space-y-2">
                <label className="text-sm font-medium text-grey-700">
                  Promo Code
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoCodeInput}
                    onChange={(e) =>
                      setPromoCodeInput(e.target.value.toUpperCase())
                    }
                    placeholder="Enter code"
                    className="flex-1 px-3 py-2 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300"
                  />
                  <Button
                    onClick={handleApplyPromo}
                    disabled={!promoCodeInput.trim() || isApplyingPromo}
                    variant="outline"
                    size="sm"
                  >
                    Apply
                  </Button>
                </div>
              </div>
            )}

            {isApplyingPromo && (
              <div className="mt-4 flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 text-sm">
                  <svg
                    className="animate-spin h-4 w-4 text-green-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                  </svg>
                  <span>Applying promo code...</span>
                </div>
              </div>
            )}

            {/* Applied Promo */}
            {promoCode && !isApplyingPromo && (
              <div className="mt-4 flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-green-600" />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-green-800">
                      {promoCode}
                    </span>
                    {!priceSummary?.appointmentPromo && (
                      <span className="text-xs text-green-600">
                        Applied during checkout
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {priceSummary?.appointmentPromo?.amount && (
                    <span className="text-sm font-medium text-green-800">
                      -
                      {formatPrice(
                        priceSummary.appointmentPromo.amount,
                        currency
                      )}
                    </span>
                  )}
                  <button
                    onClick={handleRemovePromo}
                    className="p-1 hover:bg-green-100 rounded"
                  >
                    <Trash2 className="h-4 w-4 text-green-600" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Total Section - Fixed at bottom */}
      {cartCount > 0 && (
        <div className="p-4 border-t border-grey-100 flex-shrink-0">
          {" "}
          {hasDepositAmount ? (
            <div className=" border border-12/5 border-green-300 rounded-[4px] p-3 mb-2">
              {priceSummary?.depositAmount && priceSummary.depositAmount > 0 ? (
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-grey-600">
                    Deposit required
                  </span>
                  <span className="text-sm text-grey-900">
                    {formatPrice(priceSummary?.depositAmount, currency)}
                  </span>
                </div>
              ) : null}
              <p className="text-xs text-grey-500">
                + processing fee (based on payment method)
              </p>
            </div>
          ) : null}

          {
            <>
              {priceSummary?.taxAmount &&
              priceSummary.taxAmount > 0 &&
              !hasDepositAmount &&
              selectedSalon?.isTaxVisible ? (
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-grey-600">
                    Tax{selectedSalon?.taxInclusivePricing ? " (incl.)" : ""}
                  </span>
                  <span className="text-grey-900">
                    {formatPrice(priceSummary.taxAmount, currency)}
                  </span>
                </div>
              ) : null}
              {/* Processing Fee */}
              {/* Loyalty/Membership Discount */}
              {priceSummary?.loyaltyDiscountAmount &&
              priceSummary.loyaltyDiscountAmount > 0 ? (
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-grey-600">
                    Membership Discount{" "}
                    {(serviceClient as any)?.clientMembership?.membership
                      ?.rewardPercentage &&
                      `${
                        (serviceClient as any).clientMembership.membership
                          .rewardPercentage
                      }%`}
                  </span>
                  <span className="text-grey-900">
                    -{formatPrice(priceSummary.loyaltyDiscountAmount, currency)}
                  </span>
                </div>
              ) : null}
              {/* General Discount (if different from promo/loyalty) */}
              {priceSummary?.discountAmount &&
              priceSummary.discountAmount > 0 &&
              priceSummary.discountAmount !==
                priceSummary?.appointmentPromo?.amount &&
              priceSummary.discountAmount !==
                priceSummary?.loyaltyDiscountAmount ? (
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-green-600">Discount</span>
                  <span className="text-green-600">
                    -{formatPrice(priceSummary.discountAmount, currency)}
                  </span>
                </div>
              ) : null}
              {/* Promo Discount */}
              {priceSummary?.appointmentPromo?.amount &&
              priceSummary.appointmentPromo.amount > 0 ? (
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-green-600">Promo Discount</span>
                  <span className="text-green-600">
                    -
                    {formatPrice(
                      priceSummary.appointmentPromo.amount,
                      currency
                    )}
                  </span>
                </div>
              ) : null}
              {/* Fallback membership discount when no priceSummary */}
              {!priceSummary && totalDiscount > 0 && (
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-amber-600 font-medium">
                    Membership Discount ({membershipRewardPercentage}%)
                  </span>
                  <span className="text-sm text-amber-600 font-medium">
                    -{formatPrice(totalDiscount, currency)}
                  </span>
                </div>
              )}
            </>
          }
          {!hasDepositAmount && (
            <>
              <div className="flex justify-between items-center pt-2 border-t border-grey-100 mb-2">
                <span className="text-base font-semibold text-grey-900">
                  Total
                </span>
                <span className="text-xl font-bold text-grey-900">
                  {formatPrice(
                    (priceSummary?.subTotal || total) + 
                    (selectedSalon?.isTaxVisible && !selectedSalon?.taxInclusivePricing ? (priceSummary?.taxAmount || 0) : 0),
                    currency
                  )}
                </span>
              </div>
              <p className="text-xs text-grey-500 mb-1">
                + processing fee (based on payment method)
              </p>
            </>
          )}
          {!giftCardCart  &&
          <p className="text-xs text-grey-500 mb-4">
            Total duration: {formatDuration(totalDuration)}
          </p> }
          {showButton &&
            (customButton ? (
              <Button
                onClick={customButton.onClick}
                disabled={customButton.disabled}
                className={
                  customButton.className ||
                  "w-full bg-green-300 hover:bg-green-600 text-white"
                }
              >
                {customButton.text}
              </Button>
            ) : (
              <Button className="w-full bg-green-300 hover:bg-green-600 text-white">
                Continue to booking
              </Button>
            ))}
        </div>
      )}
    </div>
  );
}
